/**
 * DocumentLoadingService - Bridge between Client-First and existing DMS/Collaboration systems
 * 
 * Handles automatic document loading with collaboration initialization for document owners.
 * Integrates with existing DMS APIs and Collaboration Engine without duplication.
 */

import axios from 'axios';
import { LocalDocumentState, AttorneyInfo, LegalDocumentType, Jurisdiction } from '../types/DocumentTypes';

export interface DMSDocument {
  document_id: string;
  title: string;
  content?: string;
  ast?: any;
  ast_version?: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  status: string;
  versions?: DMSDocumentVersion[];
}

export interface DMSDocumentVersion {
  version_id: string;
  version_number: number;
  content: string;
  ast?: any;
  created_by: string;
  created_at: string;
}

export interface CollaborationState {
  collaborators: Array<{
    user_id: string;
    role: string;
    permissions: any;
    status: string;
  }>;
  settings: {
    mode: string;
    allowComments: boolean;
    allowTrackChanges: boolean;
    autoSave: boolean;
  };
  sessions: Array<{
    user_id: string;
    status: string;
    joined_at: string;
    last_activity: string;
  }>;
}

export interface ClientFirstDocumentData {
  documentId: string;
  title: string;
  content: string;
  attorney: AttorneyInfo;
  collaborationInitialized: boolean;
  ownerHasAccess: boolean;
  version: number;
  lastModified: string;
  documentType?: LegalDocumentType;
  jurisdiction?: Jurisdiction;
}

export class DocumentLoadingService {
  private apiBaseUrl: string;
  private authToken: string;

  constructor(apiBaseUrl: string, authToken: string) {
    this.apiBaseUrl = apiBaseUrl;
    this.authToken = authToken;
  }

  /**
   * Extract realmId from JWT token (matching useDocumentView pattern)
   */
  private extractRealmFromToken(): string | undefined {
    try {
      if (!this.authToken) return undefined;
      const payload = JSON.parse(atob(this.authToken.split('.')[1] || 'e30='));
      if (payload?.realm) return payload.realm as string;
      if (payload?.iss && typeof payload.iss === 'string') {
        const m = payload.iss.match(/\/realms\/([^/]+)/);
        if (m) return m[1];
      }
    } catch (error) {
      console.warn('[DocumentLoadingService] Failed to extract realm from token:', error);
    }
    return undefined;
  }

  /**
   * Load document with automatic collaboration setup
   */
  async loadDocument(documentId: string, attorney: AttorneyInfo, versionId?: string): Promise<ClientFirstDocumentData> {
    console.log(`[DocumentLoadingService] Loading document ${documentId} for attorney ${attorney.id}${versionId ? ` (version: ${versionId})` : ''}`);

    try {
      // 1. Load document from DMS
      const dmsDocument = await this.loadFromDMS(documentId, versionId);
      console.log(`[DocumentLoadingService] DMS document loaded: ${dmsDocument.title}`);

      // 2. Ensure collaboration access (auto-initialize if needed)
      const collaborationState = await this.ensureCollaborationAccess(documentId, attorney);
      console.log(`[DocumentLoadingService] Collaboration access ensured`);

      // 3. Convert to Client-First format
      const clientFirstData = this.convertToClientFirstFormat(dmsDocument, collaborationState, attorney, versionId);
      console.log(`[DocumentLoadingService] Document converted to Client-First format`);

      return clientFirstData;

    } catch (error) {
      console.error(`[DocumentLoadingService] Error loading document ${documentId}:`, error);
      throw new Error(`Failed to load document: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Load document from existing DMS API
   */
  private async loadFromDMS(documentId: string, versionId?: string): Promise<DMSDocument> {
    try {
      // Extract realmId from token (matching useDocumentView pattern)
      const realmId = this.extractRealmFromToken();
      
      // Build URL with realmId and optional versionId query parameters
      const queryParams = new URLSearchParams();
      if (realmId) {
        queryParams.set('realmId', realmId);
      }
      if (versionId) {
        queryParams.set('versionId', versionId);
      }
      
      const url = `${this.apiBaseUrl}/api/v1/dms/documents/${documentId}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      console.log(`[DocumentLoadingService] Loading from URL: ${url}`);
      
      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${this.authToken}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Contract-Version': '1.0.0'
        }
      });

      // Handle the actual DMS API response format: {document: DocumentMeta}
      const documentData = response.data?.document || response.data?.data || response.data;
      
      if (!documentData) {
        console.error('[DocumentLoadingService] API Response:', response.data);
        throw new Error('Invalid DMS response format - missing document data');
      }

      return documentData;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 404) {
          throw new Error(`Document ${documentId}${versionId ? ` version ${versionId}` : ''} not found`);
        } else if (error.response?.status === 403) {
          throw new Error(`Access denied to document ${documentId}${versionId ? ` version ${versionId}` : ''}`);
        }
      }
      throw new Error(`Failed to load document from DMS: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Ensure collaboration access - initialize if needed, add user as collaborator
   */
  private async ensureCollaborationAccess(documentId: string, attorney: AttorneyInfo): Promise<CollaborationState> {
    try {
      // First, try to get existing collaboration state
      return await this.getCollaborationState(documentId);
    } catch (error) {
      console.log(`[DocumentLoadingService] Collaboration API not available, using fallback state for document ${documentId}`);
      
      // Return a fallback collaboration state if APIs don't exist yet
      return {
        collaborators: [{
          user_id: attorney.id,
          role: 'owner',
          permissions: {
            canEdit: true,
            canComment: true,
            canTrackChanges: true,
            canAcceptReject: true,
            canShare: true,
            canManageUsers: true
          },
          status: 'active'
        }],
        settings: {
          mode: 'realtime',
          allowComments: true,
          allowTrackChanges: true,
          autoSave: true
        },
        sessions: [{
          user_id: attorney.id,
          status: 'active',
          joined_at: new Date().toISOString(),
          last_activity: new Date().toISOString()
        }]
      };
    }
  }

  /**
   * Get collaboration state from existing API
   */
  private async getCollaborationState(documentId: string): Promise<CollaborationState> {
    const response = await axios.get(`${this.apiBaseUrl}/api/v1/documents/${documentId}/collaboration`, {
      headers: {
        'Authorization': `Bearer ${this.authToken}`,
        'Content-Type': 'application/json'
      }
    });

    return response.data.data;
  }

  /**
   * Initialize collaboration using existing API
   */
  private async initializeCollaboration(documentId: string): Promise<void> {
    await axios.post(`${this.apiBaseUrl}/api/v1/documents/${documentId}/collaboration`, {
      mode: 'realtime',
      allowComments: true,
      allowTrackChanges: true,
      autoSave: true
    }, {
      headers: {
        'Authorization': `Bearer ${this.authToken}`,
        'Content-Type': 'application/json'
      }
    });
  }

  /**
   * Add user as collaborator using existing API
   */
  private async addUserAsCollaborator(documentId: string, attorney: AttorneyInfo): Promise<void> {
    await axios.post(`${this.apiBaseUrl}/api/v1/documents/${documentId}/collaborators`, {
      userId: attorney.id,
      role: 'owner',
      permissions: {
        canEdit: true,
        canComment: true,
        canTrackChanges: true,
        canAcceptReject: true,
        canShare: true,
        canManageUsers: true
      }
    }, {
      headers: {
        'Authorization': `Bearer ${this.authToken}`,
        'Content-Type': 'application/json'
      }
    });
  }

  /**
   * Convert DMS document to Client-First format
   */
  private convertToClientFirstFormat(
    dmsDocument: DMSDocument, 
    collaborationState: CollaborationState, 
    attorney: AttorneyInfo,
    versionId?: string
  ): ClientFirstDocumentData {
    // Get the specific version content if versionId is provided, otherwise use latest
    let targetVersion;
    let content = '';
    
    if (versionId && dmsDocument.versions) {
      // Find the specific version by ID
      targetVersion = dmsDocument.versions.find(v => v.version_id === versionId);
      if (targetVersion) {
        content = targetVersion.content || '';
        console.log(`[DocumentLoadingService] Using specific version ${versionId}: ${targetVersion.version_number}`);
      } else {
        console.warn(`[DocumentLoadingService] Version ${versionId} not found, falling back to latest`);
        targetVersion = dmsDocument.versions[0];
        content = targetVersion?.content || dmsDocument.content || '';
      }
    } else {
      // Use the latest version (first in array)
      targetVersion = dmsDocument.versions?.[0];
      content = targetVersion?.content || dmsDocument.content || '';
      console.log(`[DocumentLoadingService] Using latest version: ${targetVersion?.version_number || 'unknown'}`);
    }

    // Check if user has access (handle missing collaborators array)
    const collaborators = collaborationState?.collaborators || [];
    const userCollaborator = collaborators.find(c => c.user_id === attorney.id);
    const ownerHasAccess = userCollaborator?.status === 'active' || true; // Default to true if no collaboration data

    return {
      documentId: dmsDocument.document_id,
      title: dmsDocument.title,
      content: content,
      attorney: attorney,
      collaborationInitialized: true,
      ownerHasAccess: ownerHasAccess,
      version: targetVersion?.version_number || 1,
      lastModified: dmsDocument.updated_at,
      documentType: this.inferDocumentType(dmsDocument.title),
      jurisdiction: 'federal' // Default, could be inferred from document metadata
    };
  }

  /**
   * Infer document type from title/content
   */
  private inferDocumentType(title: string): LegalDocumentType {
    const titleLower = title.toLowerCase();
    
    if (titleLower.includes('contract') || titleLower.includes('agreement')) {
      return 'contract';
    } else if (titleLower.includes('brief') || titleLower.includes('motion')) {
      return 'brief';
    } else if (titleLower.includes('pleading') || titleLower.includes('complaint')) {
      return 'pleading';
    } else if (titleLower.includes('memo') || titleLower.includes('memorandum')) {
      return 'memo';
    }
    
    return 'contract'; // Default fallback
  }

  /**
   * Save document back to DMS system
   */
  async saveDocument(documentState: LocalDocumentState): Promise<void> {
    console.log(`[DocumentLoadingService] Saving document ${documentState.documentId}`);

    try {
      // Update document content via DMS API
      await axios.put(`${this.apiBaseUrl}/api/v1/dms/documents/${documentState.documentId}`, {
        content: documentState.text,
        ast: this.convertToAST(documentState),
        ast_version: '1.0',
        lastModified: new Date().toISOString(),
        modifiedBy: documentState.attorney.id
      }, {
        headers: {
          'Authorization': `Bearer ${this.authToken}`,
          'Content-Type': 'application/json'
        }
      });

      console.log(`[DocumentLoadingService] Document ${documentState.documentId} saved successfully`);
    } catch (error) {
      console.error(`[DocumentLoadingService] Error saving document:`, error);
      throw new Error(`Failed to save document: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Convert Client-First state to AST format for DMS compatibility
   */
  private convertToAST(documentState: LocalDocumentState): any {
    // Simple conversion - in production this would be more sophisticated
    const paragraphs = documentState.text.split('\n').map((text, index) => ({
      type: 'paragraph',
      text: text,
      index: index
    }));

    return {
      blocks: paragraphs,
      version: documentState.version,
      lastModified: new Date().toISOString()
    };
  }
}

export default DocumentLoadingService;
